import nudetect from '@mc-alberta/nudetect'
import { getCurrentInstance, ref } from '@vue/composition-api'
import * as VuexCompositionHelpers from 'vuex-composition-helpers/dist'
import { ERROR_SOURCE, ROUTES, VALIDATION_CHANNEL } from '@/constants'

export default function useBindUserData() {
  const { proxy } = getCurrentInstance()

  /**
   * VUEX
   */

  const storeState = VuexCompositionHelpers.useState(['identity', 'cards', 'authentication'])

  const storeGetters = VuexCompositionHelpers.useGetters([
    'isRegisterUserFlow',
    'serviceErrorResponse',
    'isDuplicateCard'
  ])

  const storeActions = VuexCompositionHelpers.useActions([
    'bindAdditionalUserData',
    'clearServiceLayerError',
    'stopAllLoaders',
    'stopLoaderWithSuccess',
    'fetchVerificationId',
    'loadCustomerProfile',
    'registerUser',
    'setLookupSessionIdFromHeader',
    'setRegistrationData',
    'clearNewCardData'
  ])

  /**
   * REACTIVE
   */

  const errorToken = ref(null)
  const nationalIdManualError = ref(null)

  /**
   * NON-REACTIVE
   */

  let error, storedPayload
  let unauthorizedFromLoadProfile = false

  /**
   * LOGIC
   */

  async function handleBind(_storedPayload) {
    storedPayload = _storedPayload
    storeActions.clearServiceLayerError()

    if (storeGetters.isRegisterUserFlow.value) {
      await storeActions.registerUser(storedPayload)
    } else {
      await storeActions.bindAdditionalUserData(storedPayload)
    }
    nudetect.clearBehaviouralData()
    handlePostBindRoute()
  }

  async function handlePostBindRoute() {
    if (storeGetters.serviceErrorResponse.value()) {
      return handleError('common.enrollmentError')
    }

    if (storeGetters.isDuplicateCard.value) {
      const lastFourDigits = storeState.cards.value?.newCard?.panLastFour
      storeActions.clearNewCardData()

      proxy.$router.push({
        name: ROUTES.ADD_CARD,
        params: {
          errorToken: 'cards.duplicateCardError',
          skipConfirmation: true,
          lastFourDigits
        }
      })
      storeActions.stopAllLoaders()
    } else if (storeGetters.isRegisterUserFlow.value) {
      await loadProfile()
    } else {
      proxy.$router.replace({
        name: ROUTES.LIST_CARDS,
        params: { skipConfirmation: true, addedNewCard: true }
      })
      storeActions.stopLoaderWithSuccess()
    }
  }

  function handleError(errorMsg = 'error.default') {
    error = storeGetters.serviceErrorResponse.value()

    if (storeGetters.isRegisterUserFlow.value) {
      handleErrorNewUser(errorMsg)
    } else {
      handleErrorReturningUser()
    }
  }

  function handleErrorReturningUser() {
    if (error.status === 400) {
      nudetect.beginBehaviouralMonitoring()
      errorToken.value = 'common.enrollmentError'
    } else {
      proxy.$router.replace({ name: ROUTES.ERROR })
    }

    storeActions.stopAllLoaders()
  }

  function handle400NewUser(errorMsg) {
    const isSourceNationalId = isNationalIdError(error)

    // bind responds with 400 and nationalIdentifier as the source in the details if invalid national id is passed
    if (isSourceNationalId) {
      nationalIdManualError.value = proxy.$t('validation.messages.national_id')
    } else {
      nudetect.beginBehaviouralMonitoring()
      errorToken.value = errorMsg
    }
  }

  function handleErrorNewUser(errorMsg = 'error.default') {
    if (error.status === 400) {
      handle400NewUser(errorMsg)
    } else if (error.status === 401) {
      nudetect.beginBehaviouralMonitoring()
      // 401 is the expected behavior of registration.  this is a happy path ~ Inkh
      return handleUnauthorizedUser()
    } else if (error.status === 403 && error.data.status === 'CANNOT_LOCATE_PROFILE') {
      proxy.$router.push({
        name: ROUTES.ENTRY,
        params: {
          skipConfirmation: true,
          manualError: 'entry.confirmEmailAndMobile'
        }
      })
    } else if (error.status === 409) {
      nudetect.beginBehaviouralMonitoring()
      // bind 409 is registration specific, set session id here instead of general error handling map
      storeActions.setLookupSessionIdFromHeader(error)
      return handleUnauthorizedUser()
    } else {
      proxy.$router.replace({ name: ROUTES.ERROR })
    }

    storeActions.stopAllLoaders()
  }

  async function loadProfile() {
    await storeActions.loadCustomerProfile()
    const errorResponse = storeGetters.serviceErrorResponse.value()

    if (errorResponse) {
      if (errorResponse.status === 401) {
        unauthorizedFromLoadProfile = true
      }
      return handleError()
    }

    proxy.$router.replace({
      name: ROUTES.PROFILE_HOME,
      params: {
        isNewUser: true,
        skipConfirmation: true
      }
    })
    storeActions.stopLoaderWithSuccess()
  }

  async function handleUnauthorizedUser() {
    storeActions.clearServiceLayerError()

    const isAdditionalAuthRequested = storeState.authentication.value.isChallengeRequested
    if (isAdditionalAuthRequested) {
      proxy.$router.push({
        name: ROUTES.PROFILE_CREATION,
        params: {
          email: storeState.identity.value.consumerIdentity.identityValue,
          logout: true,
          skipConfirmation: true
        }
      })

      return storeActions.stopLoaderWithSuccess()
    }

    const validationChannel = isBindReturningConflictStatus()
      ? VALIDATION_CHANNEL.EMAIL
      : VALIDATION_CHANNEL.SMS
    await storeActions.fetchVerificationId(validationChannel)
    if (storeGetters.serviceErrorResponse.value()) {
      return handleError()
    }

    proxy.$router.push({
      name: ROUTES.VERIFY_ACCOUNT,
      params: {
        is409: isBindReturningConflictStatus(),
        onSuccess: onVerificationSuccess,
        skipConfirmation: true,
        showStepTracker: true
      }
    })

    storeActions.stopAllLoaders()
  }

  function isBindReturningConflictStatus() {
    return error.status === 409
  }

  function isNationalIdError(error) {
    const { data } = error
    return data.details?.some(({ source }) => source === ERROR_SOURCE.NATIONAL_ID)
  }

  async function onVerificationSuccess() {
    storeActions.setRegistrationData(null)
    storeActions.clearServiceLayerError()

    if (!unauthorizedFromLoadProfile) {
      await storeActions.registerUser(storedPayload)

      if (storeGetters.serviceErrorResponse.value()) {
        proxy.$router.replace({ name: ROUTES.ERROR, params: { logout: true } })
        storeActions.stopAllLoaders()
        return
      }
    }

    await loadProfile()
  }

  return {
    errorToken,
    handleBind,
    nationalIdManualError
  }
}
